import { Injectable } from '@angular/core';
import { CognitoAccessToken, CognitoIdToken, CognitoRefreshToken, CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import { Observable } from 'rxjs/Observable';
import { CognitoAuthService } from './cognitoAuth.service';
import { v4 as uuid } from 'uuid';
import { HttpClient } from '@angular/common/http';
import { Params } from '@angular/router';

export interface LoggedInCallback {
  isLoggedIn(message: string, loggedIn: boolean): void;
}

export interface IdentityProvider {
  providerName: string;
  providerType: string;
  identifiers?: string[];
}

@Injectable()
export class ItonicsAuthService {
  public static AuthSystemUrl = 'https://lunch.itonicsit.de';
  // private static IdentitiesUrl: string = "https://a08za1nid8.execute-api.ap-south-1.amazonaws.com/qa/api/identityProviders";
  private static IdentitiesUrl = 'https://7oqmb9k329.execute-api.ap-south-1.amazonaws.com/qa/api/identityProviders';

  private _providers: IdentityProvider[] = [];
  private _idToken: string;
  private _currentUser: CognitoUser;

  constructor(private cognitoAuthService: CognitoAuthService, private http: HttpClient) {}

  getCurrentUser() {
    if (!this._currentUser) {
      this._currentUser = this.cognitoAuthService.getUserPool().getCurrentUser();
    }
    return this._currentUser;
  }

  getUserDetails(uid) {}
  get idToken() {
    return this._idToken;
  }

  set idToken(token: string) {
    this._idToken = token;
  }

  getProviders(): Observable<IdentityProvider[]> {
    return new Observable<IdentityProvider[]>((observer) => {
      if (this._providers.length > 0) {
        observer.next(this._providers);
      } else {
        this.http.get(ItonicsAuthService.IdentitiesUrl).subscribe(
          (providers: IdentityProvider[]) => {
            this._providers = providers;
            observer.next(this._providers);
          },
          (error) => observer.error(error.error.message)
        );
      }
    });
  }

  getSamlSignInUrlForClient(tenant: string, state: string): Observable<string> {
    return new Observable<string>((observer) => {
      const identifierFilter = (provider) => provider.identifiers.indexOf(tenant) !== -1;

      this.getProviders().subscribe(
        (providers: IdentityProvider[]) => {
          const provider = providers.filter((provider) => provider.providerType === 'SAML').find(identifierFilter);

          if (provider) {
            const socialSignInUrl = this.cognitoAuthService.createSocialSignInUrl(
              state,
              ItonicsAuthService.AuthSystemUrl,
              null,
              provider.identifiers[0]
            );
            observer.next(
              this.cognitoAuthService.createSocialSignInUrl(state, ItonicsAuthService.AuthSystemUrl, null, provider.identifiers[0])
            );
            observer.complete();
          }
          observer.error('No saml identity provider found');
        },
        (error) => observer.error(error)
      );
    });
  }

  getSignInUrlForClient(): string {
    return `${ItonicsAuthService.AuthSystemUrl}/authorize?tenant=${this.getTenant().client}.${
      this.getTenant().stage
    }&state=${this.getState()}`;
  }

  createSignOutUrl() {
    return `${ItonicsAuthService.AuthSystemUrl}/logout?tenant=${this.getTenant().client}.${this.getTenant().stage}`;
  }

  getTenant() {
    return {
      client: window.location.host.split('.')[0],
      stage: window.location.host.split('.')[1]
    };
  }

  getState() {
    const key = `${this.getBaseUrl()}_state`;
    let state = localStorage.getItem(key);
    if (!state) {
      state = uuid();
      localStorage.setItem(key, state);
    }
    return state;
  }

  getBaseUrl() {
    return window.location.protocol + '//' + window.location.hostname + (window.location.port ? ':' + window.location.port : '');
  }

  setAuthSessionFromUrlIfExists(params: Params) {
    if (params != null && params.id_token && params.access_token) {
      if (params.id_token && params.access_token && params.state === this.getState()) {
        const tokenData = JwtHelper.decodeToken(params.id_token);
        if (tokenData['cognito:username']) {
          const username = tokenData['cognito:username'];
          const IdToken = new CognitoIdToken({ IdToken: params.id_token });
          const AccessToken = new CognitoAccessToken({ AccessToken: params.access_token });
          const RefreshToken = new CognitoRefreshToken({ RefreshToken: null });

          const session = new CognitoUserSession({
            IdToken,
            AccessToken,
            RefreshToken
          });

          const cognitoUser = new CognitoUser({
            Username: username,
            Pool: this.cognitoAuthService.getUserPool()
          });

          cognitoUser.setSignInUserSession(session);
          return true;
        }
      }
    }
    return false;
  }

  isAuthenticated(callback: LoggedInCallback) {
    if (callback == null) {
      Error('Callback in isAuthenticated() cannot be null');
    }

    const cognitoUser = this.getCurrentUser();

    if (cognitoUser != null) {
      cognitoUser.getSession((err, session) => {
        if (err || !session.isValid()) {
          callback.isLoggedIn(err, false);
        } else {
          this.idToken = session.getIdToken().getJwtToken();
          callback.isLoggedIn(err, true);
        }
      });
    } else {
      callback.isLoggedIn("Can't retrieve the CurrentUser", false);
    }
  }

  getCurrentUserDetails() {
    return Observable.create((observer) => {
      const cognitoUser = this.getCurrentUser();

      if (cognitoUser != null) {
        cognitoUser.getSession(function(err, session) {
          if (err) {
            observer.error(null);
          } else {
            cognitoUser.getUserAttributes(function(err, result) {
              if (err) {
                observer.error(null);
              } else {
                const user = {};
                result.forEach((attribute) => {
                  user[attribute.getName()] = attribute.getValue();
                });
                observer.next(user);
              }
            });
          }
        });
      } else {
        observer.error(null);
      }
    });
  }

  getCurrentUserGrp() {
    return Observable.create((observer) => {
      const cognitoUser = this.getCurrentUser();

      if (cognitoUser != null) {
        cognitoUser.getSession(function(err, session) {
          if (err) {
            observer.error(null);
          } else {
            const usrGrpLst = session.getIdToken().payload;
            const userGrp = usrGrpLst['cognito:groups'];
            observer.next(userGrp);
          }
        });
      } else {
        observer.error(null);
      }
    });
  }

  logout() {
    // invalidate tokens
    this.getCurrentUser().globalSignOut({
      onSuccess: (msg) => true,
      onFailure: (error) => console.error(error)
    });
    // clear local cahe
    this.getCurrentUser().signOut();
    this.idToken = null;

    // logout in idp too
    window.location.href = this.createSignOutUrl();
  }
}

export class JwtHelper {
  private static urlBase64Decode(str: string) {
    let output = str.replace(/-/g, '+').replace(/_/g, '/');
    switch (output.length % 4) {
      case 0:
        break;
      case 2:
        output += '==';
        break;
      case 3:
        output += '=';
        break;
      default:
        // tslint:disable-next-line:no-string-throw
        throw 'Illegal base64url string!';
    }
    return decodeURIComponent((<any>window).escape(window.atob(output)));
  }

  public static decodeToken(token: string = '') {
    if (token === null || token === '') {
      return { upn: '' };
    }
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('JWT must have 3 parts');
    }
    const decoded = JwtHelper.urlBase64Decode(parts[1]);
    if (!decoded) {
      throw new Error('Cannot decode the token');
    }
    return JSON.parse(decoded);
  }
}



// WEBPACK FOOTER //
// ./src/app/services/itonicsAuth.service.ts